/**
 * 梦灵日记。
 *
 * 与其他页面不同，这一页不是表格——角色日记是**读物**，不是数据行。
 * 它是角色连续存在的主要载体（旧库 444 天不间断），运营要读的是内容本身，
 * 所以做成「左侧目录 + 右侧正文」的阅读器。
 *
 * ## 结构上的一条红线
 *
 * `CharacterDiary` **不含 userId / relationshipId 列**，且有
 * `CHECK (deidentified = true)`。因此这一页没有、也不会有按用户筛选的入口。
 * 页面顶部的「全产品共享」标记就是在提醒这件事：
 * 这里记的是整个产品共同经历的一天，不属于任何单个用户。
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type Paged } from "../../services/api";
import { useAuth } from "../../services/auth";
import { useCharacter } from "../../services/character";
import { fmtNumber } from "../../components/format";

/**
 * 一篇角色日记的完整字段。与服务端 `characterDiaryDto` 一一对应。
 *
 * 导出是因为今日设定日历的日记弹窗复用了下面的 `DiaryDetail`——同一套阅读版式
 * 只该有一份，两边分叉的话弹窗里会莫名其妙少一块统计。
 */
export interface CharacterDiary {
  id: string;
  date: string;
  title: string | null;
  content: string | null;
  summary: string | null;
  plan: string | null;
  schedule: unknown;
  journal: unknown;
  tags: string[];
  memoryTags: string[];
  characterMessageCount: number;
  characterTokenCount: number;
  contactsNum: number;
  encountersNum: number;
  characterAffinitySnapshot: number;
  deidentified: boolean;
  status: string;
  canRegenerate: boolean;
}


interface Overview {
  total: number;
  firstDate: string | null;
  lastDate: string | null;
  years: { year: number; count: number }[];
}

interface RegenerationAccepted {
  jobId: string;
  status: string;
  dateString: string;
  alreadyQueued: boolean;
}

interface RegenerationStatus {
  jobId: string;
  status: "pending" | "leased" | "running" | "succeeded" | "dead" | "failed";
  attempts: number;
  maxAttempts: number;
  lastError: string | null;
  hasContent: boolean;
  indexReady: boolean;
}

const PAGE_SIZE = 30;

export function CharacterDiariesPage() {
  const { message } = App.useApp();
  const { can } = useAuth();
  const { characterId } = useCharacter();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [year, setYear] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [regenerationOpen, setRegenerationOpen] = useState(false);
  const [regenerationReason, setRegenerationReason] = useState("");
  const [regenerationJobId, setRegenerationJobId] = useState<string | null>(null);

  const scope = characterId ? { characterId } : {};

  const overview = useQuery({
    queryKey: ["characterDiaryOverview", characterId],
    queryFn: () => api.get<Overview>("/api/v1/admin/diaries/character/overview", scope),
  });

  const list = useQuery({
    queryKey: ["characterDiaries", characterId, search, year, page],
    queryFn: () =>
      api.get<Paged<CharacterDiary>>("/api/v1/admin/diaries/character", {
        ...scope,
        search: search || undefined,
        year,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => list.data?.items ?? [], [list.data]);

  // 默认选中第一篇；筛选变化后如果选中项已不在结果里，改选新的第一篇。
  useEffect(() => {
    if (items.length === 0) return;
    if (!selectedId || !items.some((d) => d.id === selectedId)) {
      setSelectedId(items[0]!.id);
    }
  }, [items, selectedId]);

  const selected = items.find((d) => d.id === selectedId) ?? null;

  const regenerate = useMutation({
    mutationFn: () =>
      api.post<RegenerationAccepted>("/api/v1/admin/diaries/character/regeneration", {
        id: selected?.id,
        reason: regenerationReason.trim(),
      }),
    onSuccess: (result) => {
      setRegenerationOpen(false);
      setRegenerationReason("");
      setRegenerationJobId(result.jobId);
      message.success(result.alreadyQueued ? "这篇日记已有重写任务，继续等待结果" : "已加入重写队列");
    },
    onError: (e: Error) => message.error(e.message),
  });

  const regenerationStatus = useQuery({
    queryKey: ["characterDiaryRegeneration", regenerationJobId],
    queryFn: () =>
      api.get<RegenerationStatus>("/api/v1/admin/diaries/character/regeneration", {
        jobId: regenerationJobId,
      }),
    enabled: Boolean(regenerationJobId),
    refetchInterval: (query) => {
      const status = (query.state.data as RegenerationStatus | undefined)?.status;
      return status === "succeeded" || status === "dead" || status === "failed" ? false : 2_000;
    },
  });

  // worker 完成后重新取真值，不在前端猜新正文。任务只有在正文与向量索引都完成后才成功。
  useEffect(() => {
    if (!regenerationJobId || !regenerationStatus.data) return;
    const result = regenerationStatus.data;
    if (result.status === "succeeded") {
      message.success("日记重写完成，向量索引已更新");
      setRegenerationJobId(null);
      void qc.invalidateQueries({ queryKey: ["characterDiaries"] });
      void qc.invalidateQueries({ queryKey: ["characterDiaryOverview"] });
      void qc.invalidateQueries({ queryKey: ["characterDiaryDetail"] });
      void qc.invalidateQueries({ queryKey: ["characterPlans"] });
    } else if (result.status === "dead" || result.status === "failed") {
      message.error(result.lastError || "日记重写失败，请到任务页查看详情");
      setRegenerationJobId(null);
    }
  }, [message, qc, regenerationJobId, regenerationStatus.data]);

  if (list.error) return <Alert type="error" showIcon message={(list.error as Error).message} />;

  return (
    // 撑满内容区：视口高 - 顶栏 56 - 内容区上下 padding 32。
    // 两栏各自内部滚动，页面本身不滚——否则右侧长正文会把左侧目录顶出视野。
    //
    // ⚠ `gridTemplateRows: minmax(0, 1fr)` 不能省。只写容器 height 的话，
    // 隐式行轨道是 `auto`，会被内容撑大到超出容器，两栏就都变回「不限高」。
    // 必须显式给一个下限为 0 的行轨道，子项才允许缩到内容高度以下并出现滚动条。
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(280px, 360px) 1fr",
        gridTemplateRows: "minmax(0, 1fr)",
        gap: 16,
        height: "calc(100vh - 88px)",
        overflow: "hidden",
      }}
    >
      <Card
        style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
        styles={{
          body: {
            padding: 16,
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          },
        }}
      >
        <Input.Search
          allowClear
          placeholder="搜索日期、标题、正文、标签"
          defaultValue={search}
          onSearch={(v) => {
            setSearch(v);
            setPage(1);
          }}
        />
        <Select
          value={year ?? "all"}
          onChange={(v) => {
            setYear(v === "all" ? undefined : Number(v));
            setPage(1);
          }}
          options={[
            { value: "all", label: `全部年份（${overview.data?.total ?? 0} 篇）` },
            ...(overview.data?.years ?? []).map((y) => ({
              value: String(y.year),
              label: `${y.year} 年（${y.count} 篇）`,
            })),
          ]}
        />

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          找到 {fmtNumber(list.data?.total ?? 0)} 篇
          {overview.data?.firstDate && (
            <>（{overview.data.firstDate} 至 {overview.data.lastDate}）</>
          )}
        </Typography.Text>

        {/* minHeight:0 是关键：flex 子项默认 min-height:auto，不加这行内部不会出现滚动条，
            而是把父容器撑破。 */}
        <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", marginInline: -4, paddingInline: 4 }}>
          <Spin spinning={list.isFetching}>
            {items.length === 0 && !list.isFetching && <Empty description="没有匹配的日记" />}
            {items.map((d) => (
              <DiaryListItem
                key={d.id}
                diary={d}
                active={d.id === selectedId}
                onClick={() => setSelectedId(d.id)}
              />
            ))}
          </Spin>
        </div>

        {(list.data?.total ?? 0) > PAGE_SIZE && (
          <Pagination
            size="small"
            current={page}
            pageSize={PAGE_SIZE}
            total={list.data?.total ?? 0}
            showSizeChanger={false}
            onChange={setPage}
            style={{ textAlign: "center", flex: "0 0 auto" }}
          />
        )}
      </Card>

      <Card
        style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
        styles={{
          body: { padding: "28px 32px", flex: "1 1 auto", minHeight: 0, overflowY: "auto" },
        }}
      >
        {selected ? (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
              <Link to={`/diaries/moments?from=${selected.date}&to=${selected.date}`}>
                <Button>查看当天动态</Button>
              </Link>
              {selected.canRegenerate && can("diary.regenerate") && (
                <Button
                  type="primary"
                  loading={regenerate.isPending || Boolean(regenerationJobId)}
                  onClick={() => setRegenerationOpen(true)}
                >
                  重新写
                </Button>
              )}
            </div>
            {regenerationJobId && (
              <Alert
                type="info"
                showIcon
                message="正在后台重写这篇日记并更新向量索引"
                description="正文和索引都完成后页面会自动刷新；失败时会按任务规则重试。"
                style={{ marginBottom: 16 }}
              />
            )}
            <DiaryDetail diary={selected} />
          </>
        ) : (
          <Empty description="选择左侧的一篇日记" />
        )}
      </Card>

      <Modal
        title={`重新写 ${selected?.date ?? ""} 的日记`}
        open={regenerationOpen}
        okText="确认重新写"
        cancelText="取消"
        confirmLoading={regenerate.isPending}
        okButtonProps={{ disabled: regenerationReason.trim().length === 0 }}
        onCancel={() => {
          if (regenerate.isPending) return;
          setRegenerationOpen(false);
          setRegenerationReason("");
        }}
        onOk={() => regenerate.mutate()}
      >
        <Typography.Paragraph type="secondary">
          将覆盖原来的标题、正文、摘要、标签和统计，但不会改当天计划。新正文写入后会立即重建向量索引；任务会调用日记与向量模型并产生费用。
        </Typography.Paragraph>
        <Input.TextArea
          autoFocus
          value={regenerationReason}
          maxLength={200}
          showCount
          rows={3}
          placeholder="填写重写原因，例如：内容过于简略，希望重新整理当天经历"
          onChange={(e) => setRegenerationReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DiaryListItem({
  diary,
  active,
  onClick,
}: {
  diary: CharacterDiary;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: "pointer",
        padding: "12px 14px",
        marginBottom: 6,
        borderRadius: "var(--miao-radius-small)",
        borderLeft: active ? "3px solid var(--miao-brand-600)" : "3px solid transparent",
        background: active ? "var(--miao-brand-100)" : "transparent",
        transition: "background 120ms",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "var(--miao-brand-700)",
          letterSpacing: "0.08em",
          fontFamily: "Georgia, serif",
        }}
      >
        {diary.date}
      </div>
      <div style={{ fontWeight: 600, margin: "2px 0 4px" }}>{diary.title || "（无标题）"}</div>
      <Typography.Paragraph
        type="secondary"
        ellipsis={{ rows: 2 }}
        style={{ fontSize: 12, marginBottom: 0 }}
      >
        {diary.summary || diary.content || "（无内容）"}
      </Typography.Paragraph>
    </div>
  );
}

/**
 * 一篇日记的阅读版式。**今日设定日历的日记弹窗也用它**，
 * 所以别往里塞只有本页才有的上下文（左右栏、翻页、选中态）。
 */
export function DiaryDetail({ diary }: { diary: CharacterDiary }) {
  const stats = [
    { label: "相遇", value: `${fmtNumber(diary.encountersNum)} 人` },
    { label: "交流", value: `${fmtNumber(diary.contactsNum)} 人` },
    { label: "消息", value: `${fmtNumber(diary.characterMessageCount)} 条` },
    { label: "交流字符", value: fmtNumber(diary.characterTokenCount) },
  ];

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <div>
        <div
          style={{
            fontSize: 13,
            letterSpacing: "0.14em",
            color: "var(--miao-brand-700)",
            fontFamily: "Georgia, serif",
            marginBottom: 14,
          }}
        >
          {diary.date}
        </div>
        <h2 className="diary-title" style={{ fontSize: 34, margin: "0 0 16px" }}>
          {diary.title || "（无标题）"}
        </h2>
        <Space size={[6, 6]} wrap>
          {diary.tags.map((t) => (
            <Tag key={t} style={{ borderRadius: 4, background: "#fff" }}>
              {t}
            </Tag>
          ))}
          {diary.tags.length === 0 && <Typography.Text type="secondary">（无标签）</Typography.Text>}
        </Space>
      </div>

      <div style={{ borderTop: "1px solid var(--miao-color-border)" }} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {stats.map((s) => (
          <div
            key={s.label}
            style={{
              background: "var(--miao-color-surface-muted)",
              borderRadius: "var(--miao-radius-medium)",
              padding: "14px 18px",
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
              {s.label}
            </Typography.Text>
            <span className="diary-title" style={{ fontSize: 24 }}>
              {s.value}
            </span>
          </div>
        ))}
      </div>

      {diary.summary && (
        <div
          style={{
            background: "var(--miao-color-surface-muted)",
            borderRadius: "var(--miao-radius-medium)",
            padding: "18px 22px",
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
            今日小记
          </Typography.Text>
          <div style={{ lineHeight: 1.9 }}>{diary.summary}</div>
        </div>
      )}

      <DiarySection label="日记正文" text={diary.content} paragraphize />
      <DiarySection label="今天的计划" text={diary.plan} />
      <DiaryJson label="日程" value={diary.schedule} />
      <DiaryJson label="随笔" value={diary.journal} />

      {diary.memoryTags.length > 0 && (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
            记忆标签
          </Typography.Text>
          <Space size={[6, 6]} wrap>
            {diary.memoryTags.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </Space>
        </div>
      )}

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        好感快照 {fmtNumber(diary.characterAffinitySnapshot)} · 去标识化{" "}
        {diary.deidentified ? "已通过" : "未通过（红线告警）"} · 状态 {diary.status}
      </Typography.Text>
    </Space>
  );
}

function readableDiaryParagraphs(text: string): string[] {
  const blocks = text
    .replace(/\r\n?/g, "\n")
    .split(/\n+/)
    .map((block) => block.trim())
    .filter(Boolean);
  if (blocks.length !== 1) return blocks;

  const sentences = blocks[0]!.match(/[^。！？!?…]+(?:[。！？!?…]+[”’」』》】）)]*|$)/g)
    ?.map((sentence) => sentence.trim())
    .filter(Boolean) ?? blocks;
  const paragraphs: string[] = [];
  let paragraph = "";
  let sentenceCount = 0;
  for (const sentence of sentences) {
    if (
      paragraph &&
      (sentenceCount >= 3 || (paragraph.length >= 60 && paragraph.length + sentence.length > 180))
    ) {
      paragraphs.push(paragraph);
      paragraph = "";
      sentenceCount = 0;
    }
    paragraph += sentence;
    sentenceCount += 1;
  }
  if (paragraph) paragraphs.push(paragraph);
  return paragraphs;
}

function DiarySection({
  label,
  text,
  paragraphize = false,
}: {
  label: string;
  text: string | null;
  paragraphize?: boolean;
}) {
  if (!text) return null;
  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 10 }}>
        {label}
      </Typography.Text>
      {paragraphize ? (
        <div style={{ lineHeight: 2, fontSize: 15 }}>
          {readableDiaryParagraphs(text).map((paragraph, index, paragraphs) => (
            <p key={index} style={{ margin: index === paragraphs.length - 1 ? 0 : "0 0 1em" }}>
              {paragraph}
            </p>
          ))}
        </div>
      ) : (
        <div style={{ lineHeight: 2, whiteSpace: "pre-wrap", fontSize: 15 }}>{text}</div>
      )}
    </div>
  );
}

function DiaryJson({ label, value }: { label: string; value: unknown }) {
  if (value == null || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
        {label}
      </Typography.Text>
      <div className="mono">{JSON.stringify(value, null, 2)}</div>
    </div>
  );
}
